/** Controllo Zion (cifrato come i messaggi normali) — allineato a ZionControl.kt */

export const MSG_READ_ACK_TYPE = 'MSG_READ_ACK'
export const MSG_DELIVER_ACK_TYPE = 'MSG_DELIVER_ACK'
export const MSG_RESYNC_REQUEST_TYPE = 'MSG_RESYNC_REQUEST'
export const MSG_KEY_OFFER_TYPE = 'MSG_KEY_OFFER'
export const MSG_VIEW_PREFIX = 'ZION::MSG_VIEW::'
/** Interop Android classic key exchange */
export const NYXIA_KEYX_PREFIX = 'NYXIA_KEYX::'
export const ZION_RESYNC_PREFIX = 'ZION::RESYNC_REQUEST::'
export const ZION_KEYX_PREFIX = 'ZION::KEYX::'

export function isZionControlPlain(plain: string): boolean {
  const t = plain.trim()
  if (!t) return false
  if (t.startsWith('ZION::')) return true
  if (t.startsWith(NYXIA_KEYX_PREFIX)) return true
  if (parseMsgReadAck(t) != null) return true
  if (parseMsgDeliverAck(t) != null) return true
  if (parseMsgResyncRequest(t) != null) return true
  if (parseMsgKeyOffer(t) != null) return true
  return false
}

/** Payload silenzioso: destinatario ha letto e distrutto il messaggio effimero. */
export function buildMsgReadAck(messageId: string, timestamp = Date.now()): string {
  return JSON.stringify({
    type: MSG_READ_ACK_TYPE,
    message_id: String(messageId),
    timestamp: String(timestamp),
  })
}

export function parseMsgReadAck(plain: string): { messageId: string; timestamp: number } | null {
  const trimmed = plain.trim()
  if (!trimmed.startsWith('{')) return null
  try {
    const obj = JSON.parse(trimmed) as Record<string, unknown>
    if (obj?.type !== MSG_READ_ACK_TYPE) return null
    const id = String(obj.message_id ?? '').trim()
    if (!id) return null
    const ts = Number(obj.timestamp)
    return {
      messageId: id,
      timestamp: Number.isFinite(ts) && ts > 0 ? ts : Date.now(),
    }
  } catch {
    return null
  }
}

/** Peer ha ricevuto e decifrato il messaggio (delivery ACK). */
export function buildMsgDeliverAck(messageId: string, timestamp = Date.now()): string {
  return JSON.stringify({
    type: MSG_DELIVER_ACK_TYPE,
    message_id: String(messageId),
    timestamp: String(timestamp),
  })
}

export function parseMsgDeliverAck(plain: string): { messageId: string; timestamp: number } | null {
  const trimmed = plain.trim()
  if (!trimmed.startsWith('{')) return null
  try {
    const obj = JSON.parse(trimmed) as Record<string, unknown>
    if (obj?.type !== MSG_DELIVER_ACK_TYPE) return null
    const id = String(obj.message_id ?? '').trim()
    if (!id) return null
    const ts = Number(obj.timestamp)
    return {
      messageId: id,
      timestamp: Number.isFinite(ts) && ts > 0 ? ts : Date.now(),
    }
  } catch {
    return null
  }
}

/** Richiede al peer di ri-offrire la chiave pubblica E2E (auto-heal). */
export function buildMsgResyncRequest(fromSessionToken: string, timestamp = Date.now()): string {
  return JSON.stringify({
    type: MSG_RESYNC_REQUEST_TYPE,
    from_session: String(fromSessionToken || '').trim(),
    timestamp: String(timestamp),
  })
}

export function parseMsgResyncRequest(
  plain: string,
): { fromSession: string; timestamp: number } | null {
  const trimmed = plain.trim()
  if (trimmed.startsWith(ZION_RESYNC_PREFIX)) {
    const fromSession = trimmed.slice(ZION_RESYNC_PREFIX.length).trim()
    return { fromSession, timestamp: Date.now() }
  }
  if (!trimmed.startsWith('{')) return null
  try {
    const obj = JSON.parse(trimmed) as Record<string, unknown>
    if (obj?.type !== MSG_RESYNC_REQUEST_TYPE) return null
    return {
      fromSession: String(obj.from_session ?? obj.fromSession ?? '').trim(),
      timestamp: Number(obj.timestamp) || Date.now(),
    }
  } catch {
    return null
  }
}

/** Peer invia la propria chiave pubblica aggiornata. */
export function buildMsgKeyOffer(publicKey: string, timestamp = Date.now()): string {
  const key = String(publicKey || '').trim()
  return JSON.stringify({
    type: MSG_KEY_OFFER_TYPE,
    public_key: key,
    timestamp: String(timestamp),
  })
}

export function parseMsgKeyOffer(plain: string): { publicKey: string; timestamp: number } | null {
  const trimmed = plain.trim()
  if (trimmed.startsWith(NYXIA_KEYX_PREFIX)) {
    const publicKey = trimmed.slice(NYXIA_KEYX_PREFIX.length).trim()
    if (!publicKey) return null
    return { publicKey, timestamp: Date.now() }
  }
  if (trimmed.startsWith(ZION_KEYX_PREFIX)) {
    const publicKey = trimmed.slice(ZION_KEYX_PREFIX.length).trim()
    if (!publicKey) return null
    return { publicKey, timestamp: Date.now() }
  }
  if (!trimmed.startsWith('{')) return null
  try {
    const obj = JSON.parse(trimmed) as Record<string, unknown>
    if (obj?.type !== MSG_KEY_OFFER_TYPE) return null
    const publicKey = String(obj.public_key ?? obj.publicKey ?? '').trim()
    if (!publicKey) return null
    return {
      publicKey,
      timestamp: Number(obj.timestamp) || Date.now(),
    }
  } catch {
    return null
  }
}

export function parseMsgViewed(plain: string): { messageId: string; expiresAt: number } | null {
  const t = plain.trim()
  if (!t.startsWith(MSG_VIEW_PREFIX)) return null
  const parts = t.slice(MSG_VIEW_PREFIX.length).split('|')
  const id = parts[0]?.trim()
  if (!id) return null
  const exp = Number(parts[1] ?? 0)
  return { messageId: id, expiresAt: Number.isFinite(exp) ? exp : 0 }
}
